import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Plus, Loader2, XCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';

const DmarcReportsCard = ({
  dmarcSummary, dmarcDays, setDmarcDays,
  dmarcUploading, uploadDmarcReport,
}) => (
  <Card className="border-[#E5E7EB]" data-testid="dmarc-reports-card">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#002FA7]" />DMARC Aggregate Reports
        </CardTitle>
        <div className="flex items-center gap-2">
          <select
            value={dmarcDays}
            onChange={e => setDmarcDays(parseInt(e.target.value))}
            className="h-8 text-xs border border-[#E5E7EB] rounded px-2"
            data-testid="dmarc-days-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <label className={`inline-flex items-center gap-1 h-8 px-3 rounded text-xs cursor-pointer border ${dmarcUploading ? 'border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed' : 'border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5'}`} data-testid="dmarc-upload-label">
            {dmarcUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Upload report
            <input
              type="file"
              accept=".xml,.gz,.zip"
              onChange={uploadDmarcReport}
              disabled={dmarcUploading}
              className="hidden"
              data-testid="dmarc-upload-input"
            />
          </label>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <p className="text-[11px] text-[#4B5563] mb-3">
        Once you publish <code className="text-[10px] bg-[#F3F4F6] px-1 rounded">rua=mailto:dmarc-reports@&lt;domain&gt;</code>,
        receivers (Google, Yahoo, Outlook) email you daily XML reports. Drop them here to see <strong>who's sending as you</strong> —
        legitimate infra should pass alignment; unknown IPs that fail = likely spoofers.
      </p>
      <details className="text-[11px] mb-3 p-2 rounded bg-[#F0F4FF] border border-[#002FA7]/20">
        <summary className="cursor-pointer font-semibold text-[#002FA7]">⚡ Auto-ingest via inbound email (no more manual uploads)</summary>
        <div className="mt-2 text-[#4B5563] space-y-1.5">
          <p>Point any inbound-email service (Resend Inbound, SendGrid, Mailgun, Postmark, SES via SNS) at the URL below. Emails from <code className="bg-white px-1 rounded">dmarc-reports@*</code>, subjects containing "DMARC", or XML/gz/zip attachments are auto-parsed and deduped through the same pipeline.</p>
          <code className="block bg-white p-2 rounded text-[10px] break-all border border-[#E5E7EB]">POST {process.env.REACT_APP_BACKEND_URL}/api/emails/inbound</code>
          <p className="text-[10px] text-[#9CA3AF]">Expected JSON: <code>{"{ from, to, subject, attachments: [{ filename, content_base64 }] }"}</code>. If <code>RESEND_WEBHOOK_SECRET</code> is set AND a <code>svix-id</code> header is present, the signature is verified.</p>
        </div>
      </details>
      {dmarcSummary && dmarcSummary.reports_count > 0 ? (
        <div className="space-y-4" data-testid="dmarc-summary">
          {/* Summary banner */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Messages</p>
              <p className="text-lg font-semibold text-[#0A0A0A]">{dmarcSummary.total_messages.toLocaleString()}</p>
            </div>
            <div className={`p-3 rounded-lg border ${dmarcSummary.alignment_pct >= 98 ? 'bg-[#34C759]/10 border-[#34C759]/30' : dmarcSummary.alignment_pct >= 90 ? 'bg-[#FF9500]/10 border-[#FF9500]/30' : 'bg-[#FF3B30]/10 border-[#FF3B30]/30'}`}>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Alignment</p>
              <p className="text-lg font-semibold" style={{ color: dmarcSummary.alignment_pct >= 98 ? '#34C759' : dmarcSummary.alignment_pct >= 90 ? '#FF9500' : '#FF3B30' }}>{dmarcSummary.alignment_pct}%</p>
            </div>
            <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Failing</p>
              <p className="text-lg font-semibold text-[#FF3B30]">{dmarcSummary.failing_messages.toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Reports</p>
              <p className="text-lg font-semibold text-[#0A0A0A]">{dmarcSummary.reports_count}</p>
            </div>
          </div>

          {/* Timeline chart */}
          {dmarcSummary.timeline.length > 1 && (
            <div>
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Daily volume (passing vs failing)</p>
              <div style={{ width: '100%', height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={dmarcSummary.timeline}>
                    <XAxis dataKey="day" fontSize={10} stroke="#9CA3AF" />
                    <YAxis fontSize={10} stroke="#9CA3AF" />
                    <RTooltip contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="passing" stroke="#34C759" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="failing" stroke="#FF3B30" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top failing IPs */}
          {dmarcSummary.top_failing_ips.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Top failing source IPs ({dmarcSummary.top_failing_ips.length})</p>
              <div className="space-y-1" data-testid="dmarc-failing-ips">
                {dmarcSummary.top_failing_ips.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded bg-[#FF3B30]/5 border border-[#FF3B30]/20 text-[11px]">
                    <XCircle className="w-3.5 h-3.5 text-[#FF3B30] flex-shrink-0" />
                    <code className="font-mono text-[#0A0A0A] flex-1 truncate">{r.source_ip}</code>
                    <span className="text-[#FF3B30] font-semibold whitespace-nowrap">{r.failing.toLocaleString()} failing</span>
                    <span className="text-[#9CA3AF] whitespace-nowrap">of {r.total.toLocaleString()}</span>
                    <span className="text-[#9CA3AF] whitespace-nowrap hidden sm:inline">last seen {r.last_seen}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top passing IPs */}
          {dmarcSummary.top_ips.filter(r => r.passing > 0).length > 0 && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-[#4B5563] hover:text-[#002FA7]">Show top sending IPs (legitimate + all)</summary>
              <div className="mt-2 space-y-1">
                {dmarcSummary.top_ips.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded bg-[#F9FAFB] border border-[#E5E7EB]">
                    {r.failing === 0 ? <CheckCircle2 className="w-3.5 h-3.5 text-[#34C759] flex-shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-[#FF9500] flex-shrink-0" />}
                    <code className="font-mono text-[#0A0A0A] flex-1 truncate">{r.source_ip}</code>
                    <span className="text-[#34C759] whitespace-nowrap">{r.passing.toLocaleString()} pass</span>
                    {r.failing > 0 && <span className="text-[#FF3B30] whitespace-nowrap">{r.failing.toLocaleString()} fail</span>}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Reporting orgs */}
          {dmarcSummary.reporting_orgs.length > 0 && (
            <p className="text-[10px] text-[#9CA3AF]">
              Reports from: {dmarcSummary.reporting_orgs.map(o => `${o.org_name} (${o.messages.toLocaleString()})`).join(' · ')}
            </p>
          )}
        </div>
      ) : (
        <div className="text-center py-6 border-2 border-dashed border-[#E5E7EB] rounded-lg">
          <Shield className="w-8 h-8 mx-auto text-[#E5E7EB] mb-2" />
          <p className="text-xs text-[#9CA3AF]">No reports ingested yet. Upload a DMARC aggregate report (.xml, .xml.gz, or .zip) to get started.</p>
        </div>
      )}
    </CardContent>
  </Card>
);

export default DmarcReportsCard;
